import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { getErrors } from './form-input-utils';

export const REVIEW_BODY_MAX_LENGTH = 2000;

type ReviewBodyInputProps = {
  /** react-hook-form field name; value is a string */
  name: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
};

/**
 * Optional review body textarea with a live character counter. Integrates with
 * react-hook-form via `Controller`.
 */
export const ReviewBodyInput = ({
  name,
  placeholder = 'Share your experience with this product…',
  maxLength = REVIEW_BODY_MAX_LENGTH,
  disabled = false,
}: ReviewBodyInputProps): JSX.Element => {
  const { control, errors } = useFormContext();
  const { hasError } = getErrors({ name, errors });

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=''
      render={({ value, onChange, onBlur }): JSX.Element => {
        const text: string = value ?? '';
        return (
          <Wrapper>
            <TextArea
              id={name}
              value={text}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={disabled}
              $hasError={hasError}
              aria-label='Your review'
            />
            <Counter aria-live='polite'>
              {text.length}/{maxLength}
            </Counter>
          </Wrapper>
        );
      }}
    />
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const TextArea = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  min-height: 96px;
  padding: 14px 16px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[30]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[80]};
  border-radius: ${({ theme }) => theme.customized.radius.sm};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[60]};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.blue[55]};
    box-shadow: ${({ theme }) => `0px 1px 5px ${theme.colors.blueShadow}`};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.red[60]};
    `}
`;

const Counter = styled.div`
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[55]};
`;
